import React, { useEffect, useState } from 'react';
import { MARKS } from '../../../constants/columns';
import { useTable, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import { carsOptions } from '../../../constants/filterOptions';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import getMarks from '../../../api/cars/getMarks';
import deleteMark from '../../../api/cars/deleteMark';

// components
import Header from '../../../components/Header/Header';
import Table from '../../../components/Table/Table';
import TableTabs from '../../../components/TableTabs/TableTabs';
import Empty from '../../../components/Empty/Empty';
import ConfirmModal from '../../../components/Modal/ConfirmModal';

import alert from '../../../helpers/alert';
import extractErrorMessage from '../../../utils/extractErrorMessage';
import { ROLE_ADMIN } from '../../../constants/roles';

// icons
import icon from '../../../brand/generated/sidebar/cars.svg';
import notFound from '../../../brand/generated/notFound.svg';
import bucketIcon from '../../../brand/generated/popover/delete.svg';

const columns = MARKS;

const CarMarks = () => {
  const navigate = useNavigate();
  const roleId = useSelector((state) => state?.user?.roleId);
  const [loading, setLoading] = useState(true);
  const [marks, setMarks] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: 10,
  });

  const table = useTable(
    {
      columns,
      data: marks,
      disableSortRemove: true,
      initialState: {
        pageSize: pagination.pageSize,
      },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { setGlobalFilter, page } = table;

  const [value, setValue] = useState('');

  const [deleteRow, setDeleteRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleNavigate = () => {
    navigate('/cars/marks/create');
  };

  // Страницу обязательно передаём в запрос: список серверный (manualPagination),
  // и без этого «Вперёд» только меняло подсветку номера, а данные оставались с
  // первой страницы.
  const init = (page = 1) => {
    setLoading(true);
    getMarks({ page })
      .then((res) => {
        setMarks(res.data.marks);
        setPagination({
          ...res.data.pagination,
          page: res.data.pagination?.pageNumber,
          pageSize: pagination.pageSize,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    init(pagination.page);
  }, [pagination.page]);

  // Отказы бэка осмысленные («у марки есть модели», «привязаны ДЦ»), поэтому
  // показываем их как есть — менеджеру понятно, что убрать сначала.
  const run = async (action, successText) => {
    setSaving(true);
    try {
      await action();
      alert.success(successText);
      setDeleteRow(null);
      init(pagination.page);
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || e?.message || 'Не удалось выполнить действие');
    } finally {
      setSaving(false);
    }
  };

  // Удаление справочника — только администратору; бэк проверяет это же сам,
  // здесь просто не показываем недоступное действие.
  const popover =
    roleId === ROLE_ADMIN
      ? [{ title: 'Удалить', icon: bucketIcon, onClick: (row) => setDeleteRow(row) }]
      : undefined;

  return (
    <>
      <Header title="Марки" icon={icon} />
      <TableTabs
        setFilter={setGlobalFilter}
        options={carsOptions}
        value={value}
        setValue={setValue}
        navigate={handleNavigate}
        button={'Добавить марку'}
        inputPlaceholder={'Быстрый поиск по маркам'}
      />
      <br />
      {!page.length && !loading ? (
        <Empty />
      ) : (
        <Table
          table={table}
          all={'марок'}
          loading={loading}
          pagination
          setPagination={setPagination}
          p={pagination}
          popover={popover}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        title="Удалить марку"
        text={`Удалить марку «${deleteRow?.name || ''}»? Действие необратимо.`}
        loading={saving}
        onConfirm={() => run(() => deleteMark({ id: deleteRow.id }), 'Марка удалена')}
      />
    </>
  );
};

export default CarMarks;
